import { useCallback, useEffect, useRef, useState } from 'react';

import { TurnstileWrapperRef } from '@/components/common/Turnstile';

/**
 * Turnstile人机验证Hook配置选项
 */
export interface UseTurnstileVerificationOptions {
  /**
   * 验证成功回调
   * @param token - Turnstile验证token,超时时为null
   */
  onSuccess: (token: string | null) => void | Promise<void>;

  /**
   * 验证失败回调(可选)
   * @param error - 错误信息
   */
  onError?: (error: Error) => void;

  /**
   * 验证超时时间(毫秒),默认10000ms
   * 超时后将自动调用onSuccess,token为null
   */
  timeout?: number;

  /**
   * 是否在超时后自动跳过验证,默认true
   */
  skipOnTimeout?: boolean;
}

/**
 * Turnstile人机验证Hook返回值
 */
export interface UseTurnstileVerificationReturn {
  // === 状态 ===
  /** 验证token,成功时为string,未验证或超时为null */
  turnstileToken: string | null;

  /** 是否正在验证中 */
  isVerifying: boolean;

  /** 是否显示托管式验证组件 */
  showManagedTurnstile: boolean;

  /** 是否显示不可见式验证组件 */
  showInvisibleTurnstile: boolean;

  // === Refs ===
  /** 不可见式验证组件的ref */
  invisibleTurnstileRef: React.RefObject<TurnstileWrapperRef>;

  // === 方法 ===
  /**
   * 启动验证流程
   * 1. 先尝试不可见式验证
   * 2. 失败时降级为托管式验证
   * 3. 超时后根据配置决定是否跳过
   */
  startVerification: () => void;

  /**
   * 重置验证状态
   * 清除token、错误信息,重置组件
   */
  resetVerification: () => void;

  /**
   * 不可见式验证成功的回调处理器
   */
  handleInvisibleSuccess: (token: string) => void;

  /**
   * 不可见式验证失败的回调处理器
   */
  handleInvisibleError: () => void;

  /**
   * 托管式验证成功的回调处理器
   */
  handleManagedSuccess: (token: string) => void;

  /**
   * 托管式验证失败的回调处理器
   */
  handleManagedError: () => void;
}

/**
 * Turnstile人机验证Hook
 *
 * 提供统一的人机验证逻辑,支持不可见式和托管式验证的自动切换
 *
 * @example
 * ```tsx
 * const {
 *   turnstileToken,
 *   isVerifying,
 *   showManagedTurnstile,
 *   showInvisibleTurnstile,
 *   invisibleTurnstileRef,
 *   startVerification,
 *   handleInvisibleSuccess,
 *   handleInvisibleError,
 *   handleManagedSuccess,
 *   handleManagedError,
 * } = useTurnstileVerification({
 *   onSuccess: (token) => {
 *     // token可能为null(超时)
 *     console.log('Verification completed:', token);
 *   },
 * });
 *
 * // 在TurnstilePortal组件中使用
 * <TurnstilePortal
 *   showInvisible={showInvisibleTurnstile}
 *   showManaged={showManagedTurnstile}
 *   invisibleRef={invisibleTurnstileRef}
 *   onInvisibleSuccess={handleInvisibleSuccess}
 *   onInvisibleError={handleInvisibleError}
 *   onManagedSuccess={handleManagedSuccess}
 *   onManagedError={handleManagedError}
 * />
 * ```
 */
export function useTurnstileVerification(
  options: UseTurnstileVerificationOptions,
): UseTurnstileVerificationReturn {
  const { onSuccess, onError, timeout = 10000, skipOnTimeout = true } = options;

  // 状态
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showManagedTurnstile, setShowManagedTurnstile] = useState(false);
  const [showInvisibleTurnstile, setShowInvisibleTurnstile] = useState(false);

  // Refs
  const invisibleTurnstileRef = useRef<TurnstileWrapperRef>(null);
  const turnstileTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isVerificationStartedRef = useRef(false);

  /**
   * 清除超时定时器
   */
  const clearTimeoutTimer = useCallback(() => {
    if (turnstileTimeoutRef.current) {
      clearTimeout(turnstileTimeoutRef.current);
      turnstileTimeoutRef.current = null;
    }
  }, []);

  /**
   * 重置验证状态
   */
  const resetVerification = useCallback(() => {
    setTurnstileToken(null);
    setIsVerifying(false);
    setShowManagedTurnstile(false);
    setShowInvisibleTurnstile(false);
    clearTimeoutTimer();
    invisibleTurnstileRef.current?.reset();
    isVerificationStartedRef.current = false;
  }, [clearTimeoutTimer]);

  /**
   * 处理不可见式验证成功
   */
  const handleInvisibleSuccess = useCallback(
    (token: string) => {
      clearTimeoutTimer();
      setTurnstileToken(token);
      setIsVerifying(false);
      setShowInvisibleTurnstile(false);
      isVerificationStartedRef.current = false;

      // 调用成功回调
      try {
        onSuccess(token);
      } catch (error) {
        onError?.(error as Error);
      }
    },
    [clearTimeoutTimer, onSuccess, onError],
  );

  /**
   * 处理不可见式验证失败 - 降级到托管式验证
   */
  const handleInvisibleError = useCallback(() => {
    setIsVerifying(false);
    setShowInvisibleTurnstile(false);
    setShowManagedTurnstile(true);
  }, []);

  /**
   * 处理托管式验证成功
   */
  const handleManagedSuccess = useCallback(
    (token: string) => {
      clearTimeoutTimer();
      setTurnstileToken(token);
      setShowManagedTurnstile(false);
      setIsVerifying(false);
      isVerificationStartedRef.current = false;

      // 调用成功回调
      try {
        onSuccess(token);
      } catch (error) {
        onError?.(error as Error);
      }
    },
    [clearTimeoutTimer, onSuccess, onError],
  );

  /**
   * 处理托管式验证失败
   */
  const handleManagedError = useCallback(() => {
    onError?.(new Error('Managed verification failed'));
  }, [onError]);

  /**
   * 启动验证流程
   */
  const startVerification = useCallback(() => {
    // 防止重复调用
    if (isVerificationStartedRef.current || isVerifying) {
      return;
    }

    isVerificationStartedRef.current = true;
    setIsVerifying(true);
    setShowInvisibleTurnstile(true);

    // 设置超时定时器
    if (skipOnTimeout) {
      turnstileTimeoutRef.current = setTimeout(() => {
        setIsVerifying(false);
        setShowInvisibleTurnstile(false);
        setShowManagedTurnstile(false);
        isVerificationStartedRef.current = false;

        // 超时时调用onSuccess,token为null
        try {
          onSuccess(null);
        } catch (error) {
          console.error('Verification timeout callback error:', error);
          onError?.(error as Error);
        }
      }, timeout);
    }

    // 延迟触发不可见式验证(等待组件渲染)
    setTimeout(() => {
      invisibleTurnstileRef.current?.execute();
    }, 100);
  }, [isVerifying, skipOnTimeout, timeout, onSuccess, onError]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      clearTimeoutTimer();
    };
  }, [clearTimeoutTimer]);

  return {
    // 状态
    turnstileToken,
    isVerifying,
    showManagedTurnstile,
    showInvisibleTurnstile,

    // Refs
    invisibleTurnstileRef,

    // 方法
    startVerification,
    resetVerification,
    handleInvisibleSuccess,
    handleInvisibleError,
    handleManagedSuccess,
    handleManagedError,
  };
}
